import React from 'react';
import { ExternalLink, Music, Smartphone, Tv, Radio, Globe, Mic, Star } from 'lucide-react';

function PlatformsPage() {
  const platforms = [
    {
      id: 'deezer',
      name: 'Deezer',
      icon: '🎵',
      category: 'streaming',
      description: "Plongez dans l'univers musical de Johnny sur Deezer avec une qualité audio exceptionnelle. Streaming haute définition sans interruption.",
      link: 'https://www.deezer.com/search/johnny',
      type: 'link',
      featured: true
    },
    {
      id: 'google-play',
      name: 'Google Play Store',
      icon: '📱',
      category: 'mobile',
      description: "L'application officielle \"100% Johnny\" sur Android. Profitez de Johnny Hallyday directement sur votre smartphone ou tablette.",
      link: 'https://play.google.com/store/apps/details?id=com.icreo.johnny100&hl=fr&pli=1',
      type: 'link'
    },
    {
      id: 'apple',
      name: 'Apple App Store',
      icon: '🍎',
      category: 'mobile',
      description: "L'application \"100% Johnny\" sur iOS, conçue pour iPhone et iPad. Interface élégante et performances optimisées.",
      link: 'https://apps.apple.com/fr/app/100-johnny/id6670359900',
      type: 'link'
    },
    {
      id: 'orange-box',
      name: 'Orange Box',
      icon: '📺',
      category: 'tv',
      description: "Transformez votre salon en temple de Johnny ! Accédez à 100% Johnny via votre décodeur Orange Box.",
      type: 'info'
    },
    {
      id: 'freebox',
      name: 'Freebox',
      icon: '📡',
      category: 'tv',
      description: "Profitez d'un accès privilégié à 100% Johnny depuis votre décodeur Freebox en haute qualité.",
      type: 'info'
    },
    {
      id: 'orange-radio',
      name: 'Orange Radio',
      icon: '🔶',
      category: 'web',
      description: "Écoutez en ligne sur le portail Orange Radio. Streaming fluide directement depuis votre navigateur.",
      link: 'https://radio.orange.com/radios/100_johnny',
      type: 'link'
    },
    {
      id: 'alexa',
      name: 'Amazon Alexa',
      icon: '🎤',
      category: 'assistant',
      description: "Dites simplement \"Alexa, lance la radio 100% Johnny\" et laissez la magie opérer.",
      link: 'https://www.amazon.fr/Easypcinfo-100-Johnny/dp/B08NK57LMF',
      type: 'link'
    },
    {
      id: 'site-officiel',
      name: 'Site Officiel',
      icon: '🌟',
      category: 'web',
      description: "Notre site officiel pour l'expérience la plus complète ! Contenus exclusifs, actualités, et player radio intégré.",
      link: 'https://100pour100johnny.com',
      type: 'link',
      featured: true
    },
    {
      id: 'welove',
      name: 'We Love Radio',
      icon: '💝',
      category: 'web',
      description: "Rejoignez la communauté We Love Radio et découvrez notre station dédiée à Johnny Hallyday.",
      link: 'https://fr.welove.radio/radio/radio-johnny-hallyday/',
      type: 'link'
    },
    {
      id: 'radioline',
      name: 'Radioline',
      icon: '📻',
      category: 'web',
      description: "Accès direct à 100% Johnny avec interface claire et qualité audio optimisée.",
      link: 'https://www.radioline.co/fr/radios/100_johnny',
      type: 'link'
    },
    {
      id: 'radio-box',
      name: 'Online Radio Box',
      icon: '📦',
      category: 'web',
      description: "100% Johnny dans un écrin technologique moderne, avec des fonctionnalités avancées.",
      link: 'https://onlineradiobox.com/fr/100johnny/?cs=fr.abclounge',
      type: 'link'
    },
    {
      id: 'mytuner',
      name: 'MyTuner Radio',
      icon: '🎛️',
      category: 'web',
      description: "Technologie de pointe pour un son cristallin. Personnalisez votre expérience d'écoute.",
      link: 'https://mytuner-radio.com/fr/radio/100-johnny-510978/',
      type: 'link'
    }
  ];

  const categories = [
    { id: 'all', label: 'Toutes', icon: '🌐' },
    { id: 'streaming', label: 'Streaming', icon: '🎵' },
    { id: 'mobile', label: 'Applications', icon: '📱' },
    { id: 'web', label: 'Web', icon: '🌍' },
    { id: 'tv', label: 'TV/Box', icon: '📺' },
    { id: 'assistant', label: 'Assistants', icon: '🎤' }
  ];

  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const filteredPlatforms = selectedCategory === 'all' 
    ? platforms 
    : platforms.filter(p => p.category === selectedCategory);

  return (
    <div className="page active">
      <div className="container">
        <div className="page-header">
          <div className="page-badge">🎧 PLATEFORMES</div>
          <h1 className="page-title">Où nous écouter</h1>
          <p className="page-subtitle">Toutes les plateformes pour profiter de 100% Johnny</p>
        </div>

        {/* Filtres par catégorie */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '30px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                padding: '10px 20px',
                background: selectedCategory === cat.id 
                  ? 'linear-gradient(135deg, #00d9ff, #ffd700)' 
                  : 'rgba(255,255,255,0.05)',
                border: selectedCategory === cat.id 
                  ? 'none' 
                  : '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                color: selectedCategory === cat.id ? '#000' : '#fff',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s',
                fontSize: '0.95rem'
              }}
              onMouseEnter={(e) => {
                if (selectedCategory !== cat.id) {
                  e.target.style.background = 'rgba(255,255,255,0.1)'
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategory !== cat.id) {
                  e.target.style.background = 'rgba(255,255,255,0.05)'
                }
              }}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>

        {/* Message d'information */}
        <div className="glass-card" style={{ 
          marginBottom: '40px',
          padding: '20px',
          background: 'rgba(0, 217, 255, 0.05)',
          border: '1px solid rgba(0, 217, 255, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: 'rgba(0, 217, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Music size={24} color="#00d9ff" />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <p style={{ 
                margin: 0, 
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.6
              }}>
                Écoutez <strong style={{ color: '#00d9ff' }}>100% Johnny</strong> partout et à tout moment ! 
                Choisissez votre plateforme préférée parmi nos nombreux partenaires de diffusion.
              </p>
            </div>
          </div>
        </div>

        {/* Grille des plateformes */}
        {filteredPlatforms.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎧</div>
            <p>Aucune plateforme dans cette catégorie</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '25px',
            marginBottom: '40px'
          }}>
            {filteredPlatforms.map((platform) => (
              <div 
                key={platform.id} 
                className="content-card"
                style={{ 
                  cursor: platform.type === 'link' ? 'pointer' : 'default',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '30px 20px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: platform.featured 
                    ? 'linear-gradient(135deg, rgba(0, 217, 255, 0.08), rgba(255, 215, 0, 0.08))'
                    : 'rgba(255, 255, 255, 0.03)',
                  border: platform.featured
                    ? '2px solid rgba(255, 215, 0, 0.3)'
                    : '1px solid rgba(255, 255, 255, 0.1)'
                }}
                onClick={() => {
                  if (platform.type === 'link' && platform.link) {
                    window.open(platform.link, '_blank', 'noopener,noreferrer')
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)'
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 217, 255, 0.3)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                {/* Badge Featured */}
                {platform.featured && (
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    background: 'linear-gradient(135deg, #ffd700, #ff8800)',
                    borderRadius: '20px',
                    padding: '5px 12px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 4px 15px rgba(255, 215, 0, 0.4)'
                  }}>
                    <Star size={12} fill="#000" />
                    RECOMMANDÉ
                  </div>
                )}

                {/* Icône principale */}
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '25px',
                  background: platform.featured
                    ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(0, 217, 255, 0.2))'
                    : 'linear-gradient(135deg, rgba(0, 217, 255, 0.15), rgba(255, 255, 255, 0.05))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '4rem',
                  marginBottom: '20px',
                  border: platform.featured
                    ? '2px solid rgba(255, 215, 0, 0.3)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: platform.featured
                    ? '0 10px 30px rgba(255, 215, 0, 0.2)'
                    : '0 10px 30px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  {platform.icon}
                </div>

                {/* Nom de la plateforme */}
                <h3 style={{ 
                  fontSize: '1.4rem', 
                  fontWeight: 800, 
                  marginBottom: '10px',
                  background: 'linear-gradient(135deg, #00d9ff, #ffd700)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1.3
                }}>
                  {platform.name}
                </h3>

                {/* Badge catégorie */}
                <div style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: 'rgba(0, 217, 255, 0.15)',
                  border: '1px solid rgba(0, 217, 255, 0.3)',
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  color: '#00d9ff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  marginBottom: '15px',
                  letterSpacing: '0.5px'
                }}>
                  {categories.find(c => c.id === platform.category)?.icon || '🌐'} {platform.category}
                </div>

                {/* Description */}
                <p style={{ 
                  color: 'rgba(255,255,255,0.7)', 
                  marginBottom: '20px',
                  lineHeight: 1.6,
                  fontSize: '0.9rem',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {platform.description}
                </p>

                {/* Bouton d'action */}
                {platform.type === 'link' ? (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #00d9ff, #ffd700)',
                    borderRadius: '12px',
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 15px rgba(0, 217, 255, 0.3)',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)'
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 217, 255, 0.5)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)'
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 217, 255, 0.3)'
                  }}
                  >
                    <ExternalLink size={18} />
                    Écouter
                  </div>
                ) : (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    width: '100%',
                    justifyContent: 'center'
                  }}>
                    <Tv size={18} />
                    Sur décodeur
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer card */}
        <div className="glass-card" style={{ 
          marginTop: '40px',
          padding: '40px 30px',
          textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.05), rgba(0, 217, 255, 0.05))',
          border: '1px solid rgba(255, 215, 0, 0.2)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #00d9ff, #ffd700)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 25px',
            boxShadow: '0 10px 30px rgba(0, 217, 255, 0.3)'
          }}>
            <Music size={40} color="#000" />
          </div>

          <h2 style={{
            fontSize: '2rem',
            fontWeight: 900,
            marginBottom: '15px',
            background: 'linear-gradient(135deg, #00d9ff, #ffd700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Rejoignez-nous dès aujourd'hui
          </h2>
          <p style={{
            fontSize: '1.05rem',
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.8,
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Profitez de la musique légendaire de <strong style={{ color: '#ffd700' }}>Johnny Hallyday</strong> où que vous soyez, 
            à tout moment. Choisissez votre plateforme préférée et laissez-vous transporter par l'univers 
            intemporel du <strong style={{ color: '#00d9ff' }}>Boss</strong> !
          </p>
        </div>

        {/* Compteur */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '30px',
          padding: '15px',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <p style={{
            color: '#00d9ff',
            fontSize: '1rem',
            fontWeight: 700,
            margin: 0
          }}>
            🎧 {filteredPlatforms.length} plateforme{filteredPlatforms.length > 1 ? 's' : ''} disponible{filteredPlatforms.length > 1 ? 's' : ''}
          </p>
        </div>

      </div>
    </div>
  );
}

export default PlatformsPage;